import * as THREE from 'three';
import type { SketchParabolaObject } from '../../../mesh/sketch/SketchParabolaObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenParabolaRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenParabolaOnCurveConstraint } from './constraints/ReferenceDrivenParabolaOnCurveConstraint';
import type {
	ReferenceDrivenParabolaRelationBinding,
	ReferenceDrivenParabolaRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenParabolaRelationService';

export type {
	ReferenceDrivenParabolaRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenParabolaRelationService';

/**
 * 参考驱动抛物线关系服务。
 */
export class ReferenceDrivenParabolaRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenParabolaRelationRuntimeRecord,
		ReferenceDrivenParabolaRelationBinding
	> {
	/**
	 * 挂载抛物线 on-curve 约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'parabola' }>;
		targetParabola: SketchParabolaObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenParabolaRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetParabola: input.targetParabola,
		});

		const record: ReferenceDrivenParabolaRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'on-curve',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetParabola.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenParabolaOnCurveConstraint({
			referenceId: input.reference.referenceId,
			projectedFocalWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.focalWorld),
			projectedApexWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.apexWorld),
			projectedCurvePointWorlds: input.projectionResult.geometryPayload.curvePointWorlds
				.map((point) => new THREE.Vector3(...point)),
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetParabola.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetParabola: input.targetParabola,
		});

		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除抛物线关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetParabola?: SketchParabolaObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetParabola.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetParabola) {
			const constraint = input.targetParabola.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenParabolaOnCurveConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetParabola.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
